/** Horizontal inset; matches main content side margins on home. */
export const HEYNOW_IOS_PANEL_INSET_X = '8px';

/** Fixed chrome: hybrid toolbar (56px + safe-area-top) + secondary-menu row (~44px); fallback 112px matches mt-28. */
export const HEYNOW_IOS_PANEL_TOP = 'var(--app-content-offset-top, calc(env(safe-area-inset-top, 0px) + 112px))';

/** Tab bar height matches libs/ui tabs (70px + home indicator when present). */
export const HEYNOW_IOS_PANEL_BOTTOM = 'var(--app-tab-bar-total, calc(70px + env(safe-area-inset-bottom, 0px)))';

const VIEWPORT_META_SELECTOR = 'meta[name="viewport"]';
const VIEWPORT_SCALE_ATTRS = /,?\s*(maximum-scale|minimum-scale|user-scalable)=[^,]*/gi; // NOSONAR: Safe regex comparison.

let savedViewportContent: string | null = null;

function stripViewportScaleAttrs(content: string): string {
  return content.replace(VIEWPORT_SCALE_ATTRS, '').replace(/,\s*,/g, ',').trim();
}

/** Applies main-content-area panel; beats vendor inline styles (verifyChatVisibility). */
export function applyIosExpandedPanelLayout(chatRoot: HTMLElement): void {
  chatRoot.style.setProperty('position', 'fixed', 'important');
  chatRoot.style.setProperty('top', HEYNOW_IOS_PANEL_TOP, 'important');
  chatRoot.style.setProperty('bottom', HEYNOW_IOS_PANEL_BOTTOM, 'important');
  chatRoot.style.setProperty('left', HEYNOW_IOS_PANEL_INSET_X, 'important');
  chatRoot.style.setProperty('right', HEYNOW_IOS_PANEL_INSET_X, 'important');
  chatRoot.style.setProperty('width', 'auto', 'important');
  chatRoot.style.setProperty('max-width', 'none', 'important');
  chatRoot.style.setProperty('height', 'auto', 'important');
  chatRoot.style.setProperty('max-height', 'none', 'important');
  chatRoot.style.setProperty('transform', 'none', 'important');
}

/** Prevents WKWebView auto-zoom while chat is open (scoped to chat session). */
export function lockViewportForHeyNowChat(doc: Document = document): void {
  const meta = doc.querySelector<HTMLMetaElement>(VIEWPORT_META_SELECTOR);
  if (!meta) {
    return;
  }
  if (savedViewportContent === null) {
    savedViewportContent = meta.getAttribute('content') ?? '';
  }
  const base = stripViewportScaleAttrs(savedViewportContent);
  meta.setAttribute('content', `${base}, maximum-scale=1, user-scalable=no`);
}

/** Restores viewport and clears persistent zoom after chat closes. */
export function unlockViewportAfterHeyNowChat(doc: Document = document): void {
  const meta = doc.querySelector<HTMLMetaElement>(VIEWPORT_META_SELECTOR);
  const win = doc.defaultView ?? globalThis;

  (doc.activeElement as HTMLElement | null)?.blur?.();

  doc.documentElement.style.zoom = '';
  doc.body.style.zoom = '';
  doc.documentElement.style.transform = '';
  doc.body.style.transform = '';
  doc.documentElement.style.removeProperty('-webkit-transform');
  doc.body.style.removeProperty('-webkit-transform');

  const restoreBase = savedViewportContent ?? meta?.getAttribute('content') ?? '';
  savedViewportContent = null;

  if (meta) {
    const base = stripViewportScaleAttrs(restoreBase);
    meta.setAttribute('content', `${base}, maximum-scale=1, user-scalable=no`);
    win.setTimeout(() => {
      meta.setAttribute('content', base);
      win.scrollTo(0, 0);
    }, 120);
    return;
  }

  win.scrollTo(0, 0);
}

/** Resets document zoom/scroll after iOS WKWebView auto-zoom on iframe input focus. */
export function resetHeyNowIosNativeViewport(doc: Document = document): void {
  unlockViewportAfterHeyNowChat(doc);
}

/** True when running inside Capacitor on iOS (Ionic platform classes on html). */
export function isHeyNowIosNativeContext(doc: Document = document): boolean {
  const root = doc.documentElement;
  return root.classList.contains('plt-hybrid') && root.classList.contains('plt-ios');
}

/** True when running inside Capacitor on any native platform (iOS or Android). */
export function isHeyNowNativeMobileContext(doc: Document = document): boolean {
  return doc.documentElement.classList.contains('plt-hybrid');
}

/** True on any mobile platform — native Capacitor (plt-hybrid) OR mobile web browser (plt-mobile). */
export function isHeyNowMobilePlatformContext(doc: Document = document): boolean {
  const root = doc.documentElement;
  return root.classList.contains('plt-hybrid') || root.classList.contains('plt-mobile');
}

const VENDOR_LAYOUT_INTERVAL_MS = 400;

/**
 * iOS native: half-width centered panel + viewport lock while open + zoom reset on close.
 * Re-applies layout on style mutations (vendor runs verifyChatVisibility every 500ms).
 */
export function attachHeyNowIosNativeHandlers(
  chatRoot: HTMLElement,
  hooks?: {
    onExpand?: () => void;
    onCollapse?: () => void;
    onLayout?: (el: HTMLElement) => void;
  }
): () => void {
  if (!isHeyNowIosNativeContext()) {
    return () => undefined;
  }

  const onLayout = hooks?.onLayout ?? applyIosExpandedPanelLayout;
  const onExpand =
    hooks?.onExpand ??
    (() => {
      lockViewportForHeyNowChat();
      onLayout(chatRoot);
    });
  const onCollapse = hooks?.onCollapse ?? (() => unlockViewportAfterHeyNowChat());

  let wasExpanded = chatRoot.classList.contains('hey-expanded');

  const syncExpandedState = (): void => {
    const isExpanded = chatRoot.classList.contains('hey-expanded');
    if (!wasExpanded && isExpanded) {
      onExpand();
    } else if (wasExpanded && !isExpanded) {
      onCollapse();
    } else if (isExpanded) {
      onLayout(chatRoot);
    }
    wasExpanded = isExpanded;
  };

  if (wasExpanded) {
    onExpand();
  }

  const observer = new MutationObserver(() => syncExpandedState());
  observer.observe(chatRoot, { attributes: true, attributeFilter: ['class', 'style'] });

  const layoutInterval = globalThis.setInterval(() => {
    if (chatRoot.classList.contains('hey-expanded')) {
      onLayout(chatRoot);
    }
  }, VENDOR_LAYOUT_INTERVAL_MS);

  return () => {
    observer.disconnect();
    globalThis.clearInterval(layoutInterval);
    if (wasExpanded) {
      onCollapse();
    }
  };
}

/** @deprecated Use attachHeyNowIosNativeHandlers */
export function attachHeyNowIosNativeCollapseHandler(
  chatRoot: HTMLElement,
  onCollapse: () => void = resetHeyNowIosNativeViewport
): () => void {
  return attachHeyNowIosNativeHandlers(chatRoot, { onCollapse });
}
